import { FileText } from 'lucide-react'
import ToolPage from '../../components/shared/ToolPage'
import type { ToolField } from '../../components/shared/ToolPage'

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'casual', label: 'Casual' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'expert', label: 'Expert' },
  { value: 'humorous', label: 'Humorous' },
]

const fields: ToolField[] = [
  { name: 'topic', label: 'Topic', type: 'text', required: true, placeholder: 'e.g. Benefits of AI in e-commerce' },
  { name: 'audience', label: 'Target Audience', type: 'text', required: true, placeholder: 'e.g. Small business owners' },
  { name: 'tone', label: 'Tone', type: 'select', options: TONE_OPTIONS, defaultValue: 'professional' },
  { name: 'keywords', label: 'Keywords', type: 'text', placeholder: 'Comma-separated keywords' },
]

function renderResult(data: any) {
  const post = data?.optimized_post || data?.result?.optimized_post || data
  const seoScore = data?.seo_score ?? data?.result?.seo_score
  const recommendations = data?.recommendations || data?.result?.recommendations || []

  return (
    <div className="space-y-4">
      {seoScore != null && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">SEO Score:</span>
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
              seoScore >= 80
                ? 'bg-green-100 text-green-700'
                : seoScore >= 60
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-red-100 text-red-700'
            }`}
          >
            {seoScore}/100
          </span>
        </div>
      )}
      {post?.title && <h2 className="text-xl font-bold text-gray-900">{post.title}</h2>}
      {post?.meta_description && (
        <p className="text-sm text-gray-500 italic">{post.meta_description}</p>
      )}
      {post?.estimated_read_time_minutes && (
        <p className="text-xs text-gray-400">{post.estimated_read_time_minutes} min read</p>
      )}
      {post?.body && (
        <div
          className="prose prose-sm max-w-none mt-4"
          dangerouslySetInnerHTML={{ __html: post.body.replace(/\n/g, '<br/>') }}
        />
      )}
      {post?.keywords_used?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-2">
          {post.keywords_used.map((k: string, i: number) => (
            <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
              {k}
            </span>
          ))}
        </div>
      )}
      {recommendations.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <h4 className="text-sm font-medium text-gray-700 mb-2">SEO Recommendations</h4>
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
            {recommendations.map((r: string, i: number) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default function BlogPage() {
  return (
    <ToolPage
      title="Blog Post Generator"
      icon={FileText}
      description="Generate SEO-optimized blog posts with AI"
      endpoint="/blog"
      fields={fields}
      renderResult={renderResult}
    />
  )
}
